import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { BACKEND_URL } from "@/lib/api";

const resolveImg = (url) => (url?.startsWith("/api/files/") ? `${BACKEND_URL}${url}` : url);

const Account = () => {
  const { user, loading, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [companions, setCompanions] = useState([]);
  const [oLoading, setOLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      api
        .get("/orders")
        .then((r) => setOrders(r.data || []))
        .catch(() => setOrders([]))
        .finally(() => setOLoading(false));
      api
        .get("/companions/mine")
        .then((r) => setCompanions(r.data || []))
        .catch(() => {});
    }
  }, [user]);

  if (loading || !user)
    return (
      <div className="px-6 md:px-12 lg:px-16 py-32 text-muted-bone text-center">
        Loading…
      </div>
    );

  return (
    <div data-testid="account-page" className="px-6 md:px-12 lg:px-16 py-16 md:py-24 max-w-5xl">
      <div className="overline mb-3">Your House</div>
      <h1 className="font-serif-display text-5xl text-bone mb-2">
        {user.name}
      </h1>
      <p className="text-muted-bone mb-12">{user.email}</p>

      <div className="flex flex-wrap gap-4 mb-16">
        <button
          data-testid="account-logout-btn"
          onClick={async () => {
            await logout();
            navigate("/");
          }}
          className="px-8 py-3 uppercase text-xs tracking-[0.22em] text-bone border hover:bg-white/5 transition"
          style={{ borderColor: "rgba(245,242,235,0.25)" }}
        >
          Sign out
        </button>
      </div>

      <div
        className="h-px mb-12"
        style={{ background: "var(--rs-border)" }}
      />

      {companions.length > 0 && (
        <>
          <div className="overline mb-6">Your Companions</div>
          <div data-testid="account-companions" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {companions.map((c) => (
              <Link
                key={c.product_id}
                data-testid={`companion-card-${c.slug}`}
                to={`/companions/${c.slug}`}
                className="group border p-5 flex items-center gap-4 hover:bg-white/5 transition"
                style={{ background: "var(--rs-surface)", borderColor: "rgba(212,175,55,0.3)" }}
              >
                <img
                  src={resolveImg(c.image)}
                  alt={c.name}
                  className="w-16 h-16 object-cover border"
                  style={{ borderColor: "var(--rs-border)" }}
                />
                <div>
                  <div className="font-serif-display text-lg text-bone">{c.name.split("—")[0].trim()}</div>
                  <div className="overline group-hover:text-bone transition">Enter the chamber →</div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}

      <div className="overline mb-6">Orders</div>
      {oLoading ? (
        <div className="text-muted-bone">Loading orders…</div>
      ) : orders.length === 0 ? (
        <div className="text-muted-bone">No orders yet. Slip something into your bag.</div>
      ) : (
        <div className="space-y-6">
          {orders.map((o) => (
            <div
              key={o.order_id}
              data-testid={`order-row-${o.order_id}`}
              className="border p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
              style={{
                background: "var(--rs-surface)",
                borderColor: "var(--rs-border)",
              }}
            >
              <div>
                <div className="overline mb-1">Order · {o.order_id.slice(-8)}</div>
                <div className="font-serif-display text-xl text-bone">
                  ${o.amount.toFixed(2)} {o.currency.toUpperCase()}
                </div>
                <div className="text-muted-bone text-sm mt-1">
                  {o.items.length} item{o.items.length !== 1 ? "s" : ""} ·{" "}
                  {new Date(o.created_at).toLocaleDateString()}
                </div>
              </div>
              <div
                className="px-3 py-1 text-xs uppercase tracking-widest"
                style={{ background: "var(--rs-oxblood)" }}
              >
                {o.status}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Account;
